import { SubscriptionType } from '../types/subscription';
import { getSubscription } from '../api/subscriptionService';

export type PermissionLevel = 'ADMIN' | 'EDIT' | 'VIEW' | 'NONE';

const permissionRank: Record<PermissionLevel, number> = {
  ADMIN: 3,
  EDIT: 2,
  VIEW: 1,
  NONE: 0,
};

export type SubscriptionPermissionsRow = {
  subscription_type: string;
  default_permission?: string | string[] | null;
  user_permission?: string | string[] | null;
};

export const firstPermissionToken = (
  value: string | string[] | null | undefined
): string | undefined => {
  if (value === undefined || value === null) return undefined;
  return Array.isArray(value) ? value[0] : value;
};

export const mapSubscriptionPermissions = (
  subscriptions: SubscriptionPermissionsRow[] = []
): SubscriptionType[] =>
  subscriptions.map(sub => {
    const orgPermission = firstPermissionToken(sub.default_permission) ?? 'NONE';
    const userPerm = firstPermissionToken(sub.user_permission) ?? 'NONE';

    return {
      type: sub.subscription_type,
      permission: { org: orgPermission, user: userPerm },
    };
  });

export const getUserSubscriptionPermissions = async (
  orgId?: string | number | null,
  userId?: string | number | null
): Promise<SubscriptionType[]> => {
  if (!orgId || !userId) return [];

  const subscriptionResponse = await getSubscription(
    String(orgId),
    String(userId)
  );
  return mapSubscriptionPermissions(
    (subscriptionResponse?.subscriptions ?? []) as SubscriptionPermissionsRow[]
  );
};

export const getPermissionBySubscriptionType = (
  permissions: SubscriptionType[] | null | undefined,
  subscriptionType: string
): SubscriptionType | null =>
  permissions?.find(permission => permission.type === subscriptionType) ?? null;

const permissionValue = (permission?: string | null): number =>
  permissionRank[permission as PermissionLevel] ?? permissionRank.NONE;

export const hasUserPermission = (
  subscription: SubscriptionType | null | undefined,
  permission: PermissionLevel
): boolean =>
  permissionValue(subscription?.permission.user) >= permissionRank[permission];

export const hasPermissionForSubscription = (
  permissions: SubscriptionType[] | null | undefined,
  subscriptionType: string,
  permission: PermissionLevel
): boolean =>
  hasUserPermission(
    getPermissionBySubscriptionType(permissions, subscriptionType),
    permission
  );

